import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { Link, useNavigate } from 'react-router-dom';
import {
  AuditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { Access, AccessDependency, EmployeeType, User } from '@prisma/client';
import { Breadcrumb, Button, Dropdown, Input, Layout, MenuProps, message, Modal, Space, theme } from 'antd';
import Table, { ColumnType } from 'antd/es/table';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { Role } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, FeatureFlags, PageLayout, Tables } from '@app/web-components';

import { AccessDependencyRenderer, ActiveColumn, EmployeeTypeRenderer, getAccessDependencyType } from '../../../common';
import { AccessSelectModal } from '../../../common/components/access-select/access-select-modal';
import {
  useAddAccess,
  useClearDependencies,
  useFindAccess,
  useSetPullMembershipThresholdOneTimeOverride,
} from '../data';

import { AccessViewPanel } from './panels';

export type AccessDetail = Access & {
  parentDependencies: AccessDependency[];
  childDependencies: AccessDependency[];
  createdByUser: User;
  updatedByUser: User;
};

export const LandingPage = () => {
  const { token } = theme.useToken();
  const { user } = Auth.useUserAuth();
  const [, messageContextHolder] = message.useMessage();
  const navigate = useNavigate();
  const clearDependencies = useClearDependencies();
  const setPullMembershipThresholdOneTimeOverride = useSetPullMembershipThresholdOneTimeOverride();
  const [accessModalView, setAccessModalView] = useState<{ open: boolean; selectedId?: number }>({ open: false });
  const { parentRef, toolbarRef, getTableHeight } = Tables.useReactiveTableHeight();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();

  const create = useAddAccess();

  const getMenuProps = (record: AccessDetail) => {
    const actions: MenuProps['items'] = [];

    if (!user) return actions;

    if (user.roles[Role.BUNDLE_ADMIN] && isFeatureEnabled('global_dependencies')) {
      actions.push({
        label: 'Manage Dependencies',
        key: 'dependencies',
        icon: <PlusOutlined />,
      });

      actions.push({
        label: 'Remove All Dependencies',
        key: 'removeDependencies',
        icon: <DeleteOutlined />,
        danger: true,
      });
    }

    if (user.roles[Role.RULE_ADMIN] || user.roles[Role.BUNDLE_ADMIN]) {
      actions.push({
        label: 'Manage Rules',
        key: 'rules',
        icon: <LockOutlined />,
      });
    }

    if (isFeatureEnabled('faceless_audit') && user.roles[Role.RULE_ADMIN]) {
      actions.push({
        label: 'Faceless Audit',
        key: 'faceless-audit',
        icon: <AuditOutlined />,
      });
    }

    if (user.roles[Role.SYSTEM_ADMIN]) {
      if (record.pullMembershipThresholdOneTimeOverride) {
        actions.push({
          label: 'Disable Membership Override',
          key: 'disableMembershipOverride',
          icon: <LockOutlined />,
        });
      } else {
        actions.push({
          label: 'Enable Membership Override',
          key: 'enableMembershipOverride',
          icon: <UnlockOutlined />,
        });
      }
    }

    return actions;
  };

  const handleDependencyFilterChange = (value: boolean | React.Key | null) => {
    switch (value) {
      case 'Has Parent':
        return {
          parentDependencies: {
            some: {},
          },
        };
      case 'Has Child':
        return {
          childDependencies: {
            some: {},
          },
        };
      case 'Has None':
        return {
          parentDependencies: {
            none: {},
          },
          childDependencies: {
            none: {},
          },
        };
      default:
        return {};
    }
  };

  const columns: ColumnType<AccessDetail>[] = [
    {
      title: 'Action',
      width: 120,
      render: (_, record) => (
        <Dropdown.Button
          size="small"
          onClick={() => handleAccessViewOpen(record.id)}
          menu={{ items: getMenuProps(record), onClick: (e) => handleActionMenuClick(e, record) }}
        >
          <Space>
            <EyeOutlined />
            Details
          </Space>
        </Dropdown.Button>
      ),
    },
    ActiveColumn,
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: true,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Source',
      width: 190,
      dataIndex: 'accessSource',
      sorter: true,
      filters: [
        { text: 'AGS Role', value: 'AGS_ROLE' },
        { text: 'AGS Entitlement', value: 'AGS_ENTITLEMENT' },
        { text: 'IEM', value: 'IEM' },
      ],
    },
    {
      title: 'Employee Type',
      width: 180,
      dataIndex: 'employeeType',
      render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
      sorter: true,
      filters: [
        { text: 'Any', value: EmployeeType.ANY },
        { text: 'Intel', value: EmployeeType.INTEL },
        { text: 'CW', value: EmployeeType.CONTINGENT_WORKER },
      ],
    },
    ...(isFeatureEnabled('global_dependencies')
      ? [
          {
            title: 'Dependencies',
            dataIndex: 'dependencies',
            render: (_: unknown, accessDetail: AccessDetail) => (
              <AccessDependencyRenderer accessDetail={accessDetail} />
            ),
            filters: [
              { text: 'Has Parent', value: 'Has Parent' },
              { text: 'Has Child', value: 'Has Child' },
              { text: 'Has None', value: 'Has None' },
            ],
          },
        ]
      : []),
    {
      title: 'Membership Override',
      dataIndex: 'pullMembershipThresholdOneTimeOverride',
      render: (pullMembershipThresholdOneTimeOverride) => (
        <Tables.BooleanRenderer value={pullMembershipThresholdOneTimeOverride} />
      ),
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false },
      ],
    },
    {
      title: 'Updated On',
      dataIndex: 'updatedOn',
      responsive: ['xxl'],
      render: (date: string) => formatIsoDate(date),
      sorter: true,
    },
  ];

  // server side table state
  const { getQuery, getCSVLinkProps, getResetFiltersButtonProps, getSearchInputProps, getTableProps } =
    Tables.useTableState<AccessDetail>('Access', columns, {
      customFilters: { dependencies: handleDependencyFilterChange },
      globalSearchFields: ['name'],
    });

  const queryResult = useFindAccess<AccessDetail>(
    getQuery({
      where: {
        accessSource: undefined,
      },
      include: {
        parentDependencies: true,
        childDependencies: true,
        createdByUser: true,
        updatedByUser: true,
      },
    }),
  );

  const handleActionMenuClick = (e: { key: string }, record: AccessDetail) => {
    switch (e.key) {
      case 'removeDependencies':
        Modal.confirm({
          title: 'Cancel dependency for access',
          centered: true,
          content: `Are you sure you want to cancel dependency for access '${record.name}'?`,
          onOk: () => {
            clearDependencies.mutate(record.id);
          },
        });
        break;
      case 'enableMembershipOverride':
        Modal.confirm({
          title: 'Enable membership override',
          centered: true,
          content: `Are you sure you want to do a one time override of the membership pull threshold? This will allow the PULL_ACCESS_MEMBERSHIPS job to pull memberships for this access even though the delta in membership counts compared to SAAF's are over the threshold.  This setting will revert to false after the job runs.`,
          onOk: () => {
            setPullMembershipThresholdOneTimeOverride.mutate({ accessId: record.id, value: true });
          },
        });
        break;
      case 'disableMembershipOverride':
        Modal.confirm({
          title: 'Disable membership override',
          centered: true,
          content: 'Are you sure you want to disable one time override of membership pull threshold?',
          onOk: () => {
            setPullMembershipThresholdOneTimeOverride.mutate({ accessId: record.id, value: false });
          },
        });
        break;
      default:
        navigate(`./${record.id}/${e.key}`);
    }
  };

  const handleAccessViewOpen = (id: number) => {
    setAccessModalView((original) => ({ ...original, open: true, selectedId: id }));
  };

  const handleAccessViewClosed = () => {
    setAccessModalView((original) => ({ ...original, open: false }));
  };

  return (
    <>
      <Modal
        title="Access"
        centered
        width={900}
        styles={{
          body: {
            height: 600,
            overflow: 'auto',
          },
        }}
        open={accessModalView.open}
        onCancel={handleAccessViewClosed}
        footer={[
          <Button key="close" type="primary" onClick={handleAccessViewClosed}>
            Close
          </Button>,
        ]}
      >
        {accessModalView.selectedId ? <AccessViewPanel id={accessModalView.selectedId} /> : undefined}
      </Modal>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Access' }]}
      />
      <Layout.Content
        ref={parentRef}
        style={{
          padding: 24,
          margin: 0,
          height: '100%',
          minHeight: 0,
          background: token.colorBgContainer,
          overflow: 'auto',
        }}
      >
        <PageLayout.Title title="Access" />

        <div style={{ width: '100%' }} ref={toolbarRef}>
          <Space style={{ marginBottom: 20 }} wrap>
            <Input placeholder="Search by access name..." {...getSearchInputProps()} />
            <AccessSelectModal
              allowNonRequestableRoles
              onChange={(newValue) => create.mutate(newValue as SourceAccessItem[])}
              type="primary"
            >
              Add Access
            </AccessSelectModal>
            <Button icon={<ReloadOutlined />} onClick={() => queryResult.refetch()}>
              Reload
            </Button>
            <Button {...getResetFiltersButtonProps()} />
            <CSVLink
              {...getCSVLinkProps(queryResult.data?.data, {
                dependencies: (_, record) => getAccessDependencyType(record),
              })}
            />
          </Space>
        </div>
        <Table
          {...getTableProps(queryResult)}
          scroll={{
            y: getTableHeight(),
          }}
        />
        {messageContextHolder}
      </Layout.Content>
    </>
  );
};
